import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  DataTable,
  Button,
  ConfirmDialog,
  Select,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../../../design-system/components';
import { formatDate } from '../../../lib/formatters';
import {
  admin_application_path,
  admin_settings_application_questions_path,
  approve_admin_application_path,
  deny_admin_application_path,
  bulk_approve_admin_applications_path,
  bulk_deny_admin_applications_path,
} from '../../../lib/routes';
const columns = [
  {
    id: 'name',
    header: 'Name',
    sortable: true,
    render: (row) => (
      <Link
        href={admin_application_path(row.id)}
        className="font-medium text-fg hover:text-fg-secondary"
      >
        {row.name}
      </Link>
    ),
  },
  {
    id: 'email',
    header: 'Email',
    accessor: 'email',
  },
  {
    id: 'campaign',
    header: 'Campaign',
    render: (row) => (
      <span className="text-sm text-fg-tertiary">
        {row.campaign_id ? `Campaign #${row.campaign_id}` : '—'}
      </span>
    ),
  },
  {
    id: 'created_at',
    header: 'Submitted',
    sortable: true,
    render: (row) => <span className="text-sm text-fg-tertiary">{formatDate(row.created_at)}</span>,
  },
  {
    id: 'actions',
    header: '',
    render: (row) => (
      <div className="flex items-center justify-end gap-2">
        <Link href={admin_application_path(row.id)}>
          <Button variant="outline" size="sm">
            Review
          </Button>
        </Link>
      </div>
    ),
  },
];
export default function Index({ affiliates, campaigns, application_required }) {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [quickAction, setQuickAction] = useState(null);
  const [quickCampaignId, setQuickCampaignId] = useState('');
  const [quickLoading, setQuickLoading] = useState(false);
  const selectedCount = selectedRows.size;
  const defaultCampaign = campaigns.find((c) => c.is_default);
  function executeBulkAction() {
    if (!bulkAction || selectedCount === 0) return;
    setBulkLoading(true);
    const affiliateIds = Array.from(selectedRows);
    const url =
      bulkAction === 'approve'
        ? bulk_approve_admin_applications_path()
        : bulk_deny_admin_applications_path();
    const data = {
      affiliate_ids: affiliateIds,
    };
    router.post(url, data, {
      onFinish: () => {
        setBulkLoading(false);
        setBulkAction(null);
        setSelectedRows(new Set());
      },
    });
  }
  function executeQuickApprove() {
    if (!quickAction || quickAction.type !== 'approve') return;
    setQuickLoading(true);
    const data = {};
    if (quickCampaignId) {
      data.campaign_id = quickCampaignId;
    }
    router.post(approve_admin_application_path(quickAction.affiliate.id), data, {
      onFinish: () => {
        setQuickLoading(false);
        setQuickAction(null);
        setQuickCampaignId('');
      },
    });
  }
  function executeQuickDeny() {
    if (!quickAction || quickAction.type !== 'deny') return;
    setQuickLoading(true);
    router.post(
      deny_admin_application_path(quickAction.affiliate.id),
      {},
      {
        onFinish: () => {
          setQuickLoading(false);
          setQuickAction(null);
        },
      },
    );
  }
  const actionColumns = [
    ...columns.slice(0, -1),
    {
      id: 'actions',
      header: '',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setQuickAction({
                type: 'approve',
                affiliate: row,
              });
              if (defaultCampaign) {
                setQuickCampaignId(String(defaultCampaign.id));
              }
            }}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={(e) => {
              e.stopPropagation();
              setQuickAction({
                type: 'deny',
                affiliate: row,
              });
            }}
          >
            Deny
          </Button>
          <Link href={admin_application_path(row.id)}>
            <Button variant="outline" size="sm">
              Review
            </Button>
          </Link>
        </div>
      ),
    },
  ];
  const bulkActionBar = (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => setBulkAction('approve')}>
        Approve Selected
      </Button>
      <Button size="sm" variant="danger" onClick={() => setBulkAction('deny')}>
        Deny Selected
      </Button>
    </div>
  );
  const bulkMessages = {
    approve: {
      title: 'Approve Applications',
      message: `Are you sure you want to approve ${selectedCount} application${selectedCount !== 1 ? 's' : ''}? They will be assigned to the default campaign.`,
      destructive: false,
    },
    deny: {
      title: 'Deny Applications',
      message: `Are you sure you want to deny ${selectedCount} application${selectedCount !== 1 ? 's' : ''}?`,
      destructive: true,
    },
  };
  return (
    <AdminLayout>
      <PageHeader
        title="Applications"
        description={`${affiliates.length} pending application${affiliates.length !== 1 ? 's' : ''}`}
        actions={
          application_required ? (
            <Link href={admin_settings_application_questions_path()}>
              <Button variant="outline">Manage Questions</Button>
            </Link>
          ) : null
        }
      />

      <div className="mt-6">
        <DataTable
          columns={actionColumns}
          data={affiliates}
          keyAccessor="id"
          selectable
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          bulkActions={bulkActionBar}
          emptyTitle="No pending applications"
          emptyDescription="New affiliate applications will appear here for review."
        />
      </div>

      {/* Bulk action confirm dialogs */}
      {bulkAction && bulkMessages[bulkAction] && (
        <ConfirmDialog
          open
          onClose={() => setBulkAction(null)}
          onConfirm={executeBulkAction}
          title={bulkMessages[bulkAction].title}
          message={bulkMessages[bulkAction].message}
          confirmLabel={bulkMessages[bulkAction].title}
          destructive={bulkMessages[bulkAction].destructive}
          loading={bulkLoading}
        />
      )}

      {/* Quick approve modal with campaign selection */}
      {quickAction?.type === 'approve' && (
        <Modal
          open
          onClose={() => {
            setQuickAction(null);
            setQuickCampaignId('');
          }}
          size="sm"
        >
          <ModalTitle>Approve {quickAction.affiliate.name}</ModalTitle>
          <ModalBody>
            <p className="mb-3 text-sm text-fg-secondary">
              Select a campaign to assign this affiliate to:
            </p>
            <Select
              label="Campaign"
              value={quickCampaignId}
              onChange={(e) => setQuickCampaignId(e.target.value)}
            >
              <option value="">Use default campaign</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.is_default ? ' (Default)' : ''}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setQuickAction(null);
                setQuickCampaignId('');
              }}
              disabled={quickLoading}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={executeQuickApprove} loading={quickLoading}>
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Quick deny confirm */}
      {quickAction?.type === 'deny' && (
        <ConfirmDialog
          open
          onClose={() => setQuickAction(null)}
          onConfirm={executeQuickDeny}
          title="Deny Application"
          message={`Are you sure you want to deny ${quickAction.affiliate.name}?`}
          confirmLabel="Deny"
          destructive
          loading={quickLoading}
        />
      )}
    </AdminLayout>
  );
}
